/**
 * Spec 004 P3 (G13): QMC's live-view session. A `MarketSession` (and its strategy twin) over a real
 * `WebSocket` to QMC's relay at `/api/sandbox/view` (same origin; Vite proxies `/api` with `ws: true`).
 *
 * Differences from echo's `/api/quant/view` (everything after the handshake is the same protocol):
 * the access token rides in the `bearer.<token>` subprotocol (a browser cannot set headers), there is
 * NO `auth` frame and no `auth.result`, so the `open` frame is sent as soon as the socket opens. With
 * no token stored the socket is opened without a subprotocol (the server may need none); a wrong token
 * refuses the upgrade or closes with 1008, both reported as `unauthorized`.
 *
 * Then a 5 s application `ping`. Liveness is judged by the relay and engine, never here: no pong
 * deadline, no open timeout (an open takes 13 s to 12 min; the user cancels with `close()`), and never
 * a close on a late or missing pong. `rate` is a frame, never a reconnect. A closing error frame or a
 * bare close surfaces as a typed `ViewFailure`; a refused later `indicators` list surfaces too but
 * leaves the view open (`closeCode: null`).
 */
import { getToken, sandboxViewUrl } from '@/app/lib/api'

import type { MarketIndicatorSpec } from './types'
import {
  ERROR_CLOSE_CODES,
  isViewErrorCode,
  NON_CLOSING_ERROR_CODES,
  type ErrorFrame,
  type MarketSession,
  type MarketSubscribe,
  type OpenMarket,
  type OpenStrategy,
  type StrategyViewSession,
  type StrategyViewSubscribe,
  type ViewClientFrame,
  type ViewFailure,
  type ViewRate,
  type ViewServerFrame,
} from './viewProtocol'

export const VIEW_PING_MS = 5000

const WS_OPEN = 1

export interface ViewSessionDeps {
  /** Injected in tests so a fake socket can stand in for the browser's `WebSocket`. */
  createSocket?: (url: string, protocols?: string[]) => WebSocket
  getToken?: () => string | null
  pingMs?: number
}

/** `ws(s)://<host>/api/sandbox/view`, same origin. */
export function viewUrl(): string {
  return sandboxViewUrl()
}

interface ViewCore {
  onFrame(cb: (frame: ViewServerFrame) => void): () => void
  onFailure(cb: (failure: ViewFailure) => void): () => void
  setRate(rate: ViewRate): void
  sendIndicators(list: MarketIndicatorSpec[]): void
  close(): void
}

function openView(openFrame: OpenMarket | OpenStrategy, deps: ViewSessionDeps): ViewCore {
  const frameListeners = new Set<(frame: ViewServerFrame) => void>()
  const failureListeners = new Set<(failure: ViewFailure) => void>()
  let closed = false
  let failed = false
  let opened = false
  let openSent = false
  let pingTimer: ReturnType<typeof setInterval> | null = null
  let pingCount = 0
  let socket: WebSocket | null = null

  function failure(f: ViewFailure): void {
    if (closed) return
    for (const cb of [...failureListeners]) cb(f)
  }

  function stopPing(): void {
    if (pingTimer !== null) clearInterval(pingTimer)
    pingTimer = null
  }

  function send(frame: ViewClientFrame): void {
    if (socket !== null && socket.readyState === WS_OPEN) socket.send(JSON.stringify(frame))
  }

  function terminal(f: ViewFailure): void {
    if (failed) return
    failed = true
    stopPing()
    failure(f)
  }

  const token = (deps.getToken ?? getToken)()
  const create =
    deps.createSocket ??
    ((url: string, protocols?: string[]) => (protocols === undefined ? new WebSocket(url) : new WebSocket(url, protocols)))
  socket = create(viewUrl(), token === null || token === '' ? undefined : [`bearer.${token}`])
  socket.addEventListener('open', () => {
    // No auth frame: the subprotocol already authenticated the upgrade.
    openSent = true
    send(openFrame)
    pingTimer = setInterval(() => {
      pingCount += 1
      send({ type: 'ping', t: `c${pingCount}` })
    }, deps.pingMs ?? VIEW_PING_MS)
  })
  socket.addEventListener('message', (event: MessageEvent) => {
    if (closed) return
    let frame: ViewServerFrame
    try {
      frame = JSON.parse(String(event.data)) as ViewServerFrame
    } catch {
      return
    }
    if (frame === null || typeof frame !== 'object' || typeof frame.type !== 'string') return
    if (frame.type === 'opened') {
      opened = true
    } else if (frame.type === 'error') {
      const err: ErrorFrame = frame
      const code = isViewErrorCode(err.code) ? err.code : null
      if (code !== null && opened && NON_CLOSING_ERROR_CODES.includes(code)) {
        failure({ code, message: err.message, closeCode: null, frame: err })
      } else {
        terminal({
          code: code ?? 'view_failed',
          message: err.message,
          closeCode: code === null ? 1011 : ERROR_CLOSE_CODES[code],
          frame: err,
        })
      }
    }
    for (const cb of [...frameListeners]) cb(frame)
  })
  socket.addEventListener('close', (event: CloseEvent) => {
    stopPing()
    if (closed || failed) return
    failed = true
    if (event.code === 1008) {
      failure({ code: 'unauthorized', message: 'Not authorized to open a live view.', closeCode: 1008, frame: null })
      return
    }
    failure({ code: 'connection_lost', message: 'The live view connection closed.', closeCode: event.code, frame: null })
  })
  socket.addEventListener('error', () => {
    // A socket error is always followed by a close; that close reports the failure.
  })

  return {
    onFrame(cb) {
      frameListeners.add(cb)
      return () => {
        frameListeners.delete(cb)
      }
    },
    onFailure(cb) {
      failureListeners.add(cb)
      return () => {
        failureListeners.delete(cb)
      }
    },
    setRate(rate) {
      if (closed) return
      if (!openSent) openFrame.rate_s = rate
      else send({ type: 'rate', rate_s: rate })
    },
    sendIndicators(list) {
      if (closed || openFrame.kind !== 'market') return
      if (!openSent) openFrame.indicators = list
      else send({ type: 'indicators', indicators: list })
    },
    close() {
      if (closed) return
      closed = true
      stopPing()
      frameListeners.clear()
      failureListeners.clear()
      if (socket !== null && (socket.readyState === 0 || socket.readyState === WS_OPEN)) socket.close(1000)
    },
  }
}

export function createMarketViewSession(args: MarketSubscribe, deps: ViewSessionDeps = {}): MarketSession {
  const core = openView(
    {
      type: 'open',
      kind: 'market',
      instrument: args.instrument,
      tf: args.tf,
      bars: args.bars,
      rate_s: args.rate_s,
      indicators: args.indicators,
    },
    deps,
  )
  return core
}

export function createStrategyViewSession(args: StrategyViewSubscribe, deps: ViewSessionDeps = {}): StrategyViewSession {
  const core = openView(
    {
      type: 'open',
      kind: 'strategy',
      slug: args.slug,
      instrument: args.instrument,
      tf: args.tf,
      bars: args.bars,
      rate_s: args.rate_s,
      params: args.params,
    },
    deps,
  )
  return { onFrame: core.onFrame, onFailure: core.onFailure, setRate: core.setRate, close: core.close }
}
